import { useContext } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { PageContext } from '@pages/MapCreateOrEdit/MapCreateOrEdit'
import { PointGroupFormValues } from '@pages/MapCreateOrEdit/types'

import { FIELD_BIND_TYPE_ENUM, MAP_DEFAULT_POINT_COLOR } from '@constants'
import { AutocompleteOption } from '@types'

type Props = {
  methods: UseFormReturn<PointGroupFormValues>
  setIsShowPointColorValueScriptDialog: (isOpen: boolean) => void
  setIsShowLabelValueScriptDialog: (isOpen: boolean) => void
}

export const useHandlers = (props: Props) => {
  const { methods, setIsShowPointColorValueScriptDialog, setIsShowLabelValueScriptDialog } = props

  const { handleCloseAddPointGroupDialog, handleUpdatePointGroups, addPointGroupDialogData } =
    useContext(PageContext)

  const {
    handleSubmit,
    formState: { isDirty },
    setValue,
    watch,
  } = methods

  const watchPointColorValue = watch('pointStyle.color.value')

  const handleSave = handleSubmit(pointGroup => {
    if (addPointGroupDialogData) {
      handleUpdatePointGroups({
        modalType: addPointGroupDialogData?.modalType,
        pointGroup,
      })
    }
  })

  const handleClose = () => {
    handleCloseAddPointGroupDialog(isDirty)
  }

  const handleChangeObjectCode = () => {
    setValue('coordinateFields.latitudeField', null)
    setValue('coordinateFields.longitudeField', null)
    setValue('getDataCommand.parameters', [])
    setValue('getDataCommand.body', [])
  }

  const handleOpenLabelValueScriptDialog = () => setIsShowLabelValueScriptDialog(true)
  const handleCloseLabelValueScriptDialog = () => setIsShowLabelValueScriptDialog(false)

  const handleOpenPointColorValueScriptDialog = () => setIsShowPointColorValueScriptDialog(true)
  const handleClosePointColorValueScriptDialog = () => setIsShowPointColorValueScriptDialog(false)

  const handleSaveLabelValueScript = (value: string) => {
    methods.setValue('label.value', value, { shouldDirty: true })

    handleCloseLabelValueScriptDialog()
  }

  const handleSavePointColorValueScript = (value: string) => {
    methods.setValue('pointStyle.color.value', value, { shouldDirty: true })

    handleClosePointColorValueScriptDialog()
  }

  const handleChangeGetDataCommand = (option: AutocompleteOption) => {
    setValue('getDataCommand.name', option as AutocompleteOption<string>, {
      shouldValidate: true,
    })
    setValue('getDataCommand.parameters', [])
    setValue('getDataCommand.body', [])
  }

  const handleChangePointColor = (value?: string) => {
    setValue('pointStyle.color.value', value as string, {
      shouldDirty: true,
    })
  }

  const handleChangePointColorBindType = (option: AutocompleteOption) => {
    if (option.id === FIELD_BIND_TYPE_ENUM.JS) {
      if (watchPointColorValue) {
        setValue('pointStyle.color.value', `return '${watchPointColorValue}'`, {
          shouldDirty: true,
        })
      }

      return
    }

    setValue('pointStyle.color.value', MAP_DEFAULT_POINT_COLOR, { shouldDirty: true })
  }

  return {
    handleSave,
    handleClose,
    handleChangeObjectCode,
    handleCloseLabelValueScriptDialog,
    handleOpenLabelValueScriptDialog,
    handleClosePointColorValueScriptDialog,
    handleOpenPointColorValueScriptDialog,
    handleSaveLabelValueScript,
    handleSavePointColorValueScript,
    handleChangeGetDataCommand,
    handleChangePointColor,
    handleChangePointColorBindType,
  }
}
